import DOMPurify from 'dompurify';

/**
 * Plain text from sanitized HTML, for previews.
 *
 * Headings, tables and figures are dropped — a heading flattened into a
 * paragraph reads as a stray fragment — and what is left is joined so that
 * sentences still end where they should once the markup is gone.
 */
export function htmlToText(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(DOMPurify.sanitize(html), 'text/html');
  const all = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  doc.body.querySelectorAll('h1, h2, h3, h4, h5, h6, table, figure').forEach((el) => el.remove());
  // Keep list items and paragraphs readable once flattened into one line
  Array.from(doc.body.querySelectorAll('p, li, blockquote'))
    .reverse()
    .forEach((el) => {
      el.after(/[.!?:;]$/.test((el.textContent || '').trim()) ? ' ' : '. ');
    });
  doc.body.querySelectorAll('div, br').forEach((el) => el.after(' '));
  return (doc.body.textContent || '').replace(/\s+/g, ' ').replace(/\s+\./g, '.').trim() || all;
}

/**
 * The first few sentences of a text, cut where a reader would stop.
 *
 * A preview that ends mid-word looks broken, so the cut falls at the end of a
 * sentence when there is one near enough, and at a word boundary otherwise.
 */
export function excerpt(text: string, max = 320): string {
  const clean = (text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const room = clean.slice(0, max);
  const sentence = Math.max(room.lastIndexOf('. '), room.lastIndexOf('! '), room.lastIndexOf('? '));
  if (sentence > max * 0.5) return room.slice(0, sentence + 1);
  const word = room.lastIndexOf(' ');
  return `${(word > 0 ? room.slice(0, word) : room).replace(/[,;:·—-]+$/, '')}…`;
}
